import axios from 'axios';
import { API_BASE_URL } from './config';

// Directus configuration - using existing API base URL
const DIRECTUS_URL = API_BASE_URL;

// Create axios instance with base configuration
const directusAPI = axios.create({
  baseURL: DIRECTUS_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public API instance (no auth required) for confirmation endpoints
const publicAPI = axios.create({
  baseURL: DIRECTUS_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
directusAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
directusAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  /**
   * Login user with username and password
   */
  login: async (username, password) => {
    try {
      const response = await directusAPI.post('/auth/login', {
        email: username, // Directus uses email field for login
        password: password,
      });
      
      const { access_token, refresh_token } = response.data.data;
      
      // Store tokens
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Login failed');
    }
  },

  /**
   * Get current user information
   */
  getCurrentUser: async () => {
    try {
      const response = await directusAPI.get('/users/me', {
        params: {
          fields: ['id', 'email', 'first_name', 'last_name', 'role', 'patient_id'],
        },
      });
      
      const userData = response.data.data;
      localStorage.setItem('user_data', JSON.stringify(userData));
      
      return userData;
    } catch (error) {
      throw new Error('Failed to fetch user data');
    }
  },

  /**
   * Logout user
   */
  logout: async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await directusAPI.post('/auth/logout', {
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
    }
  },

  /**
   * Refresh access token
   */
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await directusAPI.post('/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token: newRefreshToken } = response.data.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      
      return access_token;
    } catch (error) {
      throw new Error('Token refresh failed');
    }
  },
};

// Patient/Appointments APIs
export const patientAPI = {
  /**
   * Get patient information by patient ID
   */
  getPatientInfo: async (patientId) => {
    try {
      const response = await directusAPI.get(`/items/patients/${patientId}`, {
        params: {
          fields: ['*', 'user_id.*'],
        },
      });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to fetch patient information');
    }
  },

  /**
   * Find patient by associated Directus user ID
   */
  getPatientByUserId: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required to fetch patient');
    }

    try {
      const response = await directusAPI.get('/items/patients', {
        params: {
          filter: {
            user_id: { _eq: userId },
          },
          limit: 1,
          fields: ['*', 'user_id.*'],
        },
      });

      return response.data.data?.[0] || null;
    } catch (error) {
      throw new Error('Failed to fetch patient by user');
    }
  },

  /**
   * Get appointment history for a patient
   */
  getAppointmentHistory: async (patientId) => {
    try {
      const response = await directusAPI.get('/items/appointments', {
        params: {
          filter: {
            patient_id: { _eq: patientId },
            appointment_date: { _lt: '$NOW' },
            status: { _in: ['completed', 'cancelled'] },
          },
          sort: ['-appointment_date'],
          fields: ['*', 'service_id.*', 'doctor_id.*'],
        },
      });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to fetch appointment history');
    }
  },

  /**
   * Get upcoming appointments for a patient
   */
  getUpcomingAppointments: async (patientId) => {
    try {
      const response = await directusAPI.get('/items/appointments', {
        params: {
          filter: {
            patient_id: { _eq: patientId },
            appointment_date: { _gte: '$NOW' },
            status: { _in: ['scheduled', 'confirmed'] },
          },
          sort: ['appointment_date'],
          fields: ['*', 'service_id.*', 'doctor_id.*'],
        },
      });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to fetch upcoming appointments');
    }
  },

  /**
   * Get patient files/documents
   */
  getPatientFiles: async (patientId) => {
    try {
      const response = await directusAPI.get('/items/patient_files', {
        params: {
          filter: {
            patient_id: { _eq: patientId },
          },
          sort: ['-date_created'],
          fields: ['*', 'file_id.*'],
        },
      });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to fetch patient files');
    }
  },

  /**
   * Get patient treatment records
   */
  getTreatmentRecords: async (patientId) => {
    try {
      const response = await directusAPI.get('/items/treatment_records', {
        params: {
          filter: {
            patient_id: { _eq: patientId },
          },
          sort: ['-treatment_date'],
          fields: ['*', 'appointment_id.*', 'doctor_id.*'],
        },
      });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to fetch treatment records');
    }
  },

  /**
   * Get appointment by ID (for confirmation page)
   * Uses public access with token verification
   * Note: This requires public read access to appointments collection in Directus
   */
  getAppointmentById: async (appointmentId, confirmationToken = null) => {
    try {
      // Use public API (no auth) - requires Directus permissions to allow public read
      const response = await publicAPI.get(`/items/appointments/${appointmentId}`, {
        params: {
          fields: ['*', 'service_id.*', 'doctor_id.*', 'patient_id.*'],
        },
      });

      const appointment = response.data.data;

      // Verify confirmation token matches if provided
      if (confirmationToken && appointment.confirmation_token !== confirmationToken) {
        throw new Error('Invalid confirmation link. Please check your message.');
      }

      return appointment;
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        throw new Error('You do not have permission to view this appointment.');
      }
      throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to fetch appointment');
    }
  },

  /**
   * Confirm an appointment (update status to 'confirmed')
   * Uses public access with token verification
   * Note: This requires public update access to appointments collection in Directus
   */
  confirmAppointment: async (appointmentId, confirmationToken = null) => {
    try {
      // First verify the token matches
      if (confirmationToken) {
        const appointmentResponse = await publicAPI.get(`/items/appointments/${appointmentId}`, {
          params: {
            fields: ['confirmation_token', 'status'],
          },
        });

        const appointment = appointmentResponse.data.data;

        if (appointment.confirmation_token !== confirmationToken) {
          throw new Error('Invalid confirmation link.');
        }

        // Check if already confirmed
        if (appointment.status === 'confirmed') {
          throw new Error('This appointment has already been confirmed.');
        }
      }

      // Update the appointment status using public API
      const response = await publicAPI.patch(
        `/items/appointments/${appointmentId}`,
        { status: 'confirmed' }
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        throw new Error('You do not have permission to confirm this appointment. Please contact the clinic.');
      }
      throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to confirm appointment');
    }
  },
};

export default directusAPI;

